import { useState } from 'react';
import type { AssetRef } from '@flashpoint/site-dna';
import { uploadLeadAsset } from '../../lib/storage';
import { mergeSiteDnaAssets } from '../../lib/firestore';
import { useAuth } from '../../auth/AuthContext';
import { logHistory } from '../../lib/historyLog';
import type { StepProps } from '../stepProps';

interface UploadState {
  percent: number;
  cancel: () => void;
  filename: string;
}

export function AssetsStep({ lead, flush }: StepProps) {
  const { user } = useAuth();
  const a = lead.siteDna.assets;

  const [active, setActive] = useState<Record<string, UploadState | undefined>>({});
  const [error, setError] = useState<string | null>(null);

  const start = async (slot: 'logo' | 'hero' | 'gallery' | 'owner', file: File) => {
    setError(null);
    const key = `${slot}_${Date.now()}`;
    try {
      const handle = uploadLeadAsset(lead.id, file, {
        slot,
        onProgress: (p) =>
          setActive((prev) => ({
            ...prev,
            [key]: { percent: p.percent, cancel: handle.cancel, filename: file.name },
          })),
      });
      setActive((prev) => ({
        ...prev,
        [key]: { percent: 0, cancel: handle.cancel, filename: file.name },
      }));
      const ref = await handle.promise;

      await flush();

      if (slot === 'gallery') {
        await mergeSiteDnaAssets(lead.id, (prev) => ({
          ...prev,
          gallery: [...(prev.gallery ?? []), ref],
        }));
      } else if (slot === 'logo') {
        await mergeSiteDnaAssets(lead.id, (prev) => ({ ...prev, logo: ref }));
      } else if (slot === 'owner') {
        await mergeSiteDnaAssets(lead.id, (prev) => ({ ...prev, owner: ref }));
      } else {
        await mergeSiteDnaAssets(lead.id, (prev) => ({ ...prev, hero: ref }));
      }

      logHistory({
        leadId: lead.id,
        user,
        kind: 'asset_upload',
        payload: { slot, bytes: ref.bytes, mimeType: ref.mimeType, downloadURL: ref.downloadURL },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      // eslint-disable-next-line no-console
      console.error('[flashpoint] asset upload failed', err);
    } finally {
      setActive((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const removeGalleryAt = async (i: number) => {
    try {
      await flush();
      await mergeSiteDnaAssets(lead.id, (prev) => ({
        ...prev,
        gallery: (prev.gallery ?? []).filter((_, idx) => idx !== i),
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      // eslint-disable-next-line no-console
      console.error('[flashpoint] gallery remove failed', err);
    }
  };

  const clearSlot = async (slot: 'logo' | 'hero' | 'owner') => {
    try {
      await flush();
      await mergeSiteDnaAssets(lead.id, (prev) => {
        const next = { ...prev };
        delete next[slot];
        return next;
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      // eslint-disable-next-line no-console
      console.error('[flashpoint] asset clear failed', err);
    }
  };

  return (
    <div className="space-y-6">
      <SlotCard label="Logo" hint="Square or wordmark, transparent PNG ideal">
        <AssetView ref_={a.logo} onClear={() => void clearSlot('logo')} />
        <FilePicker accept="image/*" onPick={(f) => void start('logo', f)} />
      </SlotCard>

      <SlotCard label="Hero" hint="Wide image (e.g. 1600x900)">
        <AssetView ref_={a.hero} onClear={() => void clearSlot('hero')} />
        <FilePicker accept="image/*" onPick={(f) => void start('hero', f)} />
      </SlotCard>

      <SlotCard label="Owner" hint="Portrait for About. Stamp initials if empty.">
        <AssetView ref_={a.owner} onClear={() => void clearSlot('owner')} />
        <FilePicker accept="image/*" onPick={(f) => void start('owner', f)} />
      </SlotCard>

      <SlotCard label="Gallery" hint="Add up to 12 images">
        {(a.gallery ?? []).length === 0 && (
          <p className="text-xs text-nm-muted">No gallery images yet.</p>
        )}
        {(a.gallery ?? []).length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
            {(a.gallery ?? []).map((ref, i) => (
              <div key={i} className="relative">
                <img
                  src={ref.downloadURL}
                  alt={ref.alt ?? ''}
                  className="aspect-square w-full rounded-md border border-nm-border object-cover"
                />
                <button
                  type="button"
                  onClick={() => void removeGalleryAt(i)}
                  className="absolute right-1 top-1 rounded bg-nm-bg/80 px-1 text-[10px] text-nm-danger hover:opacity-80"
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
        <FilePicker accept="image/*" onPick={(f) => void start('gallery', f)} />
      </SlotCard>

      {Object.entries(active).map(([key, st]) =>
        st ? (
          <div key={key} className="rounded-md border border-nm-border bg-nm-moss/30 p-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="font-mono text-nm-ink">{st.filename}</span>
              <button type="button" onClick={() => st.cancel()} className="text-nm-danger hover:opacity-80">
                Cancel
              </button>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded bg-nm-chrome">
              <div
                className="h-full bg-flashpoint-spark transition-[width]"
                style={{ width: `${Math.round(st.percent * 100)}%` }}
              />
            </div>
          </div>
        ) : null,
      )}

      {error && (
        <div className="rounded-md border border-nm-danger/30 bg-nm-danger-bg px-3 py-2 text-xs text-nm-danger">
          {error}
        </div>
      )}
    </div>
  );
}

function SlotCard({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2 rounded-xl border border-nm-border bg-nm-moss/30 p-4">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-widest text-nm-muted">{label}</span>
        {hint && <span className="text-[11px] text-nm-muted">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function AssetView({ ref_, onClear }: { ref_?: AssetRef; onClear: () => void }) {
  if (!ref_) return null;
  return (
    <div className="flex items-center gap-3">
      <img
        src={ref_.downloadURL}
        alt={ref_.alt ?? ''}
        className="h-16 w-16 rounded-md border border-nm-border object-cover"
      />
      <div className="flex-1 text-xs">
        <div className="text-nm-ink">{ref_.mimeType}</div>
        <div className="text-nm-muted">{(ref_.bytes / 1024).toFixed(1)} KB</div>
      </div>
      <button type="button" onClick={onClear} className="text-xs text-nm-danger hover:opacity-80">
        Replace
      </button>
    </div>
  );
}

function FilePicker({
  accept,
  onPick,
}: {
  accept: string;
  onPick: (file: File) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-center rounded-md border border-dashed border-nm-border px-4 py-3 text-xs text-nm-ink hover:border-nm-accent">
      <input
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.currentTarget.value = '';
        }}
      />
      Tap to upload
    </label>
  );
}
